import AccountSelect from "../components/AccountSelect";
import FXPreview from "../components/FXPreview";
import CurrencySelect from "../components/CurrencySelect";
import { useState, useEffect, useRef } from "react";
import { api } from "../App";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyInput } from "../components/ui/currency-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Plus, Trash2, Edit2, DollarSign, Building2, PiggyBank, Landmark, HandCoins, Search, SlidersHorizontal, X } from "lucide-react";
import "./AssetsPage.css";

const formatCurrency = (amount, currency = "COP") => {
  return formatMoney(amount, currency, { style: "decimal" });
};

const requestId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const OPERATION_NAMES = { opening: "Apertura", payment: "Pago recibido", deposit: "Depósito", withdrawal: "Retiro", contribution: "Aporte", liquidation: "Liquidación del CDT", sale: "Venta del activo completo", valuation: "Valoración", adjustment: "Conciliación", edit: "Edición", reversal: "Anulación", archive: "Archivo" };
const DEFAULT_OPERATION = { loan: "payment", savings: "deposit", pension: "contribution", severance: "contribution", cdt: "liquidation", good: "sale", stock: "sale" };

const ASSET_TYPE_OPTIONS = [
  { value: "cdt", label: "CDT" },
  { value: "yield_accounts", label: "Cuentas de rentabilidad" },
  { value: "loans", label: "Préstamos" },
  { value: "goods", label: "Bienes" },
  { value: "stocks", label: "Acciones" },
  { value: "severance", label: "Cesantías" },
  { value: "pension", label: "Pensión" },
];

const EDITOR_TYPE_LABELS = {
  loans: "Préstamo",
  cdt: "CDT",
  yield_accounts: "Cuenta de rentabilidad",
  severance: "Cesantías",
  pension: "Pensión",
  goods: "Bien",
  stocks: "Acciones",
};

const ASSET_API_PATHS = {
  loans: "/assets/loans",
  cdt: "/assets/cdts",
  yield_accounts: "/assets/savings-accounts",
  severance: "/assets/severance",
  pension: "/assets/pensions",
  goods: "/assets/goods",
  stocks: "/assets/stocks",
};

const EDITOR_TYPE_BY_ASSET_TYPE = {
  loan: "loans",
  cdt: "cdt",
  savings: "yield_accounts",
  severance: "severance",
  pension: "pension",
  good: "goods",
  stock: "stocks",
};

const ASSET_ICONS = {
  cdt: "/activos/cdt.png",
  yield_accounts: "/activos/cuentas-rentabilidad.png",
  loans: "/activos/prestamos.png",
  goods: "/activos/bienes.png",
  stocks: "/activos/acciones.png",
};

function BalanceOriginChoice({ value, onChange }) {
  return (
    <fieldset className="aorra-assets-balance-origin">
      <legend>¿De dónde salió el dinero para este activo?</legend>
      <p>Así mantenemos correcto tu saldo disponible y tu balance.</p>
      <div className="aorra-assets-balance-toggle">
        <button
          type="button"
          aria-pressed={value === true}
          className={value === true ? "is-selected" : ""}
          onClick={() => onChange(true)}
        >
          Salió de mi balance
        </button>
        <button
          type="button"
          aria-pressed={value === false}
          className={value === false ? "is-selected" : ""}
          onClick={() => onChange(false)}
        >
          Es una inversión anterior
        </button>
      </div>
      <small>
        {value === true
          ? "Registraremos un egreso vinculado y descontaremos este valor de tu saldo disponible."
          : value === false
            ? "Solo registraremos el activo; tu saldo disponible no cambiará."
            : "Selecciona una opción para continuar."}
      </small>
    </fieldset>
  );
}

function AssetsEmptyIllustration() {
  return (
    <svg
      className="aorra-assets-empty-illustration"
      viewBox="0 0 420 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M79 99h92c11 0 20 9 20 20s-9 20-20 20h-38c-11 0-20 9-20 20s9 20 20 20H65c-11 0-20-9-20-20s9-20 20-20H39c-11 0-20-9-20-20s9-20 20-20h40Z" fill="#F3F7FF" />
      <path d="M340 111h41c11 0 20 9 20 20s-9 20-20 20h-16c-11 0-20 9-20 20s9 20 20 20h-67c-11 0-20-9-20-20s9-20 20-20h-19c-11 0-20-9-20-20s9-20 20-20h61Z" fill="#F3F7FF" />
      <ellipse cx="211" cy="229" rx="111" ry="16" fill="#F3F7FF" />

      <path d="m112 117 99-72 99 72" stroke="#396BE9" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M132 110v112h158V110l-79-57-79 57Z" fill="white" stroke="#396BE9" strokeWidth="4" strokeLinejoin="round" />
      <path d="M229 79h38v143h-38z" fill="#E8F0FE" />
      <rect x="153" y="128" width="32" height="32" rx="4" fill="#F8FAFF" stroke="#75A4FE" strokeWidth="3" />
      <rect x="237" y="128" width="32" height="32" rx="4" fill="#F8FAFF" stroke="#75A4FE" strokeWidth="3" />
      <path d="M159 144h20M169 134v20M243 144h20M253 134v20" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M184 222v-46c0-8 6-14 14-14h26c8 0 14 6 14 14v46" fill="#F8FAFF" stroke="#396BE9" strokeWidth="3" />
      <circle cx="226" cy="193" r="3" fill="#396BE9" />
      <path d="M103 222h216" stroke="#396BE9" strokeWidth="4" strokeLinecap="round" />

      <path d="M322 67c0-12 10-22 22-22 4-14 17-24 33-24 20 0 36 16 36 36v2c9 3 15 11 15 21 0 12-10 22-22 22h-61c-13 0-23-10-23-23V67Z" fill="white" stroke="#75A4FE" strokeWidth="3" strokeLinecap="round" transform="translate(-34 10) scale(.75)" />
      <path d="M87 72c0-9 7-16 16-16 3-11 13-18 25-18 15 0 27 12 27 27v2c7 2 12 8 12 15 0 9-8 17-17 17h-46c-9 0-17-8-17-17V72Z" fill="white" stroke="#75A4FE" strokeWidth="3" />
      <path d="m327 42 4 10 10 4-10 4-4 10-4-10-10-4 10-4 4-10Z" fill="white" stroke="#75A4FE" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

export default function AssetsPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("loans");
  const [assetSearch, setAssetSearch] = useState("");
  const [assetTypeFilter, setAssetTypeFilter] = useState("all");
  const [assetWizardOpen, setAssetWizardOpen] = useState(false);
  const [assetWizardStep, setAssetWizardStep] = useState(1);
  const [assetRates, setAssetRates] = useState({});
  const [assetCurrency, setAssetCurrency] = useState("");
  const [newAssetName, setNewAssetName] = useState("");
  const [newAssetType, setNewAssetType] = useState("");
  const [wizardEditingAsset, setWizardEditingAsset] = useState(null);
  const [assetHistoryOpen, setAssetHistoryOpen] = useState(false);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [assetMovement, setAssetMovement] = useState({ amount: "", currency: "COP", type: "deposit", rates: {} });
  const [savingMovement, setSavingMovement] = useState(false);
  const movementLock = useRef(false);
  const [savingAsset, setSavingAsset] = useState(false);
  const saveLock = useRef(false);
  const [openingDetails, setOpeningDetails] = useState({ account_id: "", transaction_date: today(), request_id: requestId(), adjustment_reason: "" });
  
  // Data states
  const [loans, setLoans] = useState([]);
  const [cdts, setCdts] = useState([]);
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [severances, setSeverances] = useState([]);
  const [pensions, setPensions] = useState([]);
  const [goods, setGoods] = useState([]);
  const [stocks, setStocks] = useState([]);
  
  // Dialog states
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [cdtDialogOpen, setCdtDialogOpen] = useState(false);
  const [savingsDialogOpen, setSavingsDialogOpen] = useState(false);
  const [severanceDialogOpen, setSeveranceDialogOpen] = useState(false);
  const [pensionDialogOpen, setPensionDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  
  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [selectedLoanForPayment, setSelectedLoanForPayment] = useState(null);
  
  // Form states
  const [loanForm, setLoanForm] = useState({ borrower_name: "", amount: "", interest_rate: "", interest_type: "monthly", due_date: "", notes: "", affects_balance: true });
  const [cdtForm, setCdtForm] = useState({ bank_name: "", amount: "", interest_rate: "", interest_type: "annual", start_date: "", maturity_date: "", notes: "", affects_balance: true });
  const [savingsForm, setSavingsForm] = useState({ account_name: "", bank_name: "", balance: "", interest_rate: "", interest_type: "annual", notes: "", affects_balance: false });
  const [severanceForm, setSeveranceForm] = useState({ fund_name: "", balance: "", employer: "", notes: "", affects_balance: false });
  const [pensionForm, setPensionForm] = useState({ fund_name: "", balance: "", contribution_type: "mandatory", employer: "", notes: "", affects_balance: false });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [goodForm, setGoodForm] = useState({ name: "", estimated_value: "", notes: "", affects_balance: false });
  const [stockForm, setStockForm] = useState({ name: "", quantity: "", current_price: "", notes: "", affects_balance: false });
  
  const currency = summary?.currency || "COP";

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryRes, loansRes, cdtsRes, savingsRes, sevRes, penRes, goodsRes, stocksRes] = await Promise.all([
        api.get("/assets/summary"),
        api.get("/assets/loans?include_archived=true"),
        api.get("/assets/cdts?include_archived=true"),
        api.get("/assets/savings-accounts?include_archived=true"),
        api.get("/assets/severance?include_archived=true"),
        api.get("/assets/pensions?include_archived=true"),
        api.get("/assets/goods?include_archived=true"),
        api.get("/assets/stocks?include_archived=true")
      ]);
      setSummary(summaryRes.data);
      setLoans(loansRes.data);
      setCdts(cdtsRes.data);
      setSavingsAccounts(savingsRes.data);
      setSeverances(sevRes.data);
      setPensions(penRes.data);
      setGoods(goodsRes.data);
      setStocks(stocksRes.data);
    } catch (error) {
      toast.error("Error al cargar activos");
    } finally {
      setLoading(false);
    }
  };

  // CRUD functions for Loans
  const saveLoan = async () => {
    try {
      if (editingItem) {
        await api.put(`/assets/loans/${editingItem.id}`, loanForm);
        toast.success("Préstamo actualizado");
      } else {
        await api.post("/assets/loans", loanForm);
        toast.success("Préstamo registrado");
      }
      setLoanDialogOpen(false);
      setEditingItem(null);
      setLoanForm({ borrower_name: "", amount: "", interest_rate: "", interest_type: "monthly", due_date: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Error al guardar préstamo");
    }
  };

  const deleteLoan = async (id) => {
    if (!confirm("¿Eliminar este préstamo?")) return;
    try {
      await api.delete(`/assets/loans/${id}`);
      toast.success("Préstamo eliminado");
      fetchAllData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const recordLoanPayment = async () => {
    if (!selectedLoanForPayment || !paymentAmount) return;
    try {
      await api.post(`/assets/loans/${selectedLoanForPayment.id}/payment?amount=${paymentAmount}`);
      toast.success("Pago registrado");
      setPaymentDialogOpen(false);
      setSelectedLoanForPayment(null);
      setPaymentAmount("");
      fetchAllData();
    } catch (error) {
      toast.error("Error al registrar pago");
    }
  };

  // CRUD functions for CDTs
  const saveCdt = async () => {
    try {
      if (editingItem) {
        await api.put(`/assets/cdts/${editingItem.id}`, cdtForm);
        toast.success("CDT actualizado");
      } else {
        await api.post("/assets/cdts", cdtForm);
        toast.success("CDT registrado");
      }
      setCdtDialogOpen(false);
      setEditingItem(null);
      setCdtForm({ bank_name: "", amount: "", interest_rate: "", interest_type: "annual", start_date: "", maturity_date: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Error al guardar CDT");
    }
  };

  const deleteCdt = async (id) => {
    if (!confirm("¿Eliminar este CDT?")) return;
    try {
      await api.delete(`/assets/cdts/${id}`);
      toast.success("CDT eliminado");
      fetchAllData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // CRUD functions for Savings Accounts
  const saveSavingsAccount = async () => {
    try {
      if (editingItem) {
        await api.put(`/assets/savings-accounts/${editingItem.id}`, savingsForm);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/assets/savings-accounts", savingsForm);
        toast.success("Cuenta registrada");
      }
      setSavingsDialogOpen(false);
      setEditingItem(null);
      setSavingsForm({ account_name: "", bank_name: "", balance: "", interest_rate: "", interest_type: "annual", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Error al guardar cuenta");
    }
  };

  const deleteSavingsAccount = async (id) => {
    if (!confirm("¿Eliminar esta cuenta?")) return;
    try {
      await api.delete(`/assets/savings-accounts/${id}`);
      toast.success("Cuenta eliminada");
      fetchAllData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // CRUD functions for Severance
  const saveSeverance = async () => {
    try {
      if (editingItem) {
        await api.put(`/assets/severance/${editingItem.id}`, severanceForm);
        toast.success("Cesantías actualizadas");
      } else {
        await api.post("/assets/severance", severanceForm);
        toast.success("Cesantías registradas");
      }
      setSeveranceDialogOpen(false);
      setEditingItem(null);
      setSeveranceForm({ fund_name: "", balance: "", employer: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Error al guardar cesantías");
    }
  };

  const deleteSeverance = async (id) => {
    if (!confirm("¿Eliminar estas cesantías?")) return;
    try {
      await api.delete(`/assets/severance/${id}`);
      toast.success("Cesantías eliminadas");
      fetchAllData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // CRUD functions for Pension
  const savePension = async () => {
    try {
      if (editingItem) {
        await api.put(`/assets/pensions/${editingItem.id}`, pensionForm);
        toast.success("Pensión actualizada");
      } else {
        await api.post("/assets/pensions", pensionForm);
        toast.success("Pensión registrada");
      }
      setPensionDialogOpen(false);
      setEditingItem(null);
      setPensionForm({ fund_name: "", balance: "", contribution_type: "mandatory", employer: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error("Error al guardar pensión");
    }
  };

  const deletePension = async (id) => {
    if (!confirm("¿Eliminar esta pensión?")) return;
    try {
      await api.delete(`/assets/pensions/${id}`);
      toast.success("Pensión eliminada");
      fetchAllData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openAssetWizard = () => {
    setEditingItem(null);
    setAssetCurrency(currency);
    setOpeningDetails({ account_id: "", transaction_date: today(), request_id: requestId(), adjustment_reason: "" });
    setAssetRates({});
    setWizardEditingAsset(null);
    setNewAssetName("");
    setNewAssetType("");
    setAssetWizardStep(1);
    setAssetWizardOpen(true);
  };

  const continueAssetWizard = () => {
    if (!newAssetName.trim()) {
      toast.error("Escribe el nombre del activo");
      return;
    }
    if (newAssetType === "cdt") setCdtForm({ bank_name: newAssetName, amount: "", interest_rate: "", interest_type: "annual", start_date: "", maturity_date: "", notes: "", affects_balance: null });
    if (newAssetType === "yield_accounts") setSavingsForm({ account_name: newAssetName, bank_name: "", balance: "", interest_rate: "", interest_type: "annual", notes: "", affects_balance: null });
    if (newAssetType === "loans") setLoanForm({ borrower_name: newAssetName, amount: "", interest_rate: "", interest_type: "monthly", due_date: "", notes: "", affects_balance: null });
    if (newAssetType === "goods") setGoodForm({ name: newAssetName, estimated_value: "", notes: "", affects_balance: null });
    if (newAssetType === "stocks") setStockForm({ name: newAssetName, quantity: "", current_price: "", notes: "", affects_balance: null });
    if (newAssetType === "severance") setSeveranceForm({ fund_name: newAssetName, balance: "", employer: "", notes: "", affects_balance: null });
    if (newAssetType === "pension") setPensionForm({ fund_name: newAssetName, balance: "", contribution_type: "mandatory", notes: "", affects_balance: null });
    setAssetWizardStep(2);
  };

  const saveWizardAsset = async () => {
    if (saveLock.current) return;
    saveLock.current = true;
    setSavingAsset(true);
    try {
      const wizardPayloads = {
        cdt: cdtForm,
        yield_accounts: savingsForm,
        loans: loanForm,
        severance: severanceForm,
        pension: pensionForm,
        goods: goodForm,
        stocks: stockForm,
      };
      const path = ASSET_API_PATHS[newAssetType];
      const payload = { ...wizardPayloads[newAssetType], ...openingDetails, transaction_date: newAssetType === "cdt" ? cdtForm.start_date : openingDetails.transaction_date, account_id: openingDetails.account_id === "unassigned" ? null : openingDetails.account_id || null, currency: assetCurrency || currency, exchange_rates: assetRates };
      if (!wizardEditingAsset && typeof payload.affects_balance !== "boolean") {
        toast.error("Indica si el dinero salió de tu balance o si es una inversión anterior");
        return;
      }
      if (wizardEditingAsset) {
        await api.put(`${path}/${wizardEditingAsset.id}`, payload);
      } else {
        await api.post(path, payload);
      }
      toast.success(wizardEditingAsset ? "Activo actualizado con historial" : "Activo registrado");
      setAssetWizardOpen(false);
      setWizardEditingAsset(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Revisa los datos del activo e inténtalo de nuevo");
      await fetchAllData();
    } finally { saveLock.current = false; setSavingAsset(false); }
  };

  const deleteWizardAsset = async () => {
    if (!wizardEditingAsset || !confirm("¿Archivar este activo? Se conservarán su historial y todos sus movimientos de dinero.")) return;
    try {
      await api.delete(`${ASSET_API_PATHS[newAssetType]}/${wizardEditingAsset.id}`);
      toast.success("Activo archivado; historial conservado");
      setAssetWizardOpen(false);
      setWizardEditingAsset(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No pudimos archivar el activo");
    }
  };

  const saveAssetMovement = async () => {
    if (movementLock.current || !historyAsset || !(Number(assetMovement.amount) > 0)) return;
    movementLock.current = true;
    setSavingMovement(true);
    try {
      const path = ASSET_API_PATHS[EDITOR_TYPE_BY_ASSET_TYPE[historyAsset.assetType]];
      await api.post(`${path}/${historyAsset.id}/operations`, {
        kind: assetMovement.type, amount: Number(assetMovement.amount), currency: assetMovement.currency,
        transaction_date: assetMovement.transaction_date, exchange_rates: assetMovement.rates,
        account_id: assetMovement.account_id === "unassigned" ? null : assetMovement.account_id || null,
        interest_amount: Number(assetMovement.interest_amount || 0),
        affects_balance: assetMovement.affects_balance !== false, request_id: assetMovement.request_id,
      });
      toast.success("Movimiento registrado en la moneda del activo");
      setAssetHistoryOpen(false);
      await fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || "No pudimos registrar el movimiento"); setAssetHistoryOpen(false); await fetchAllData(); }
    finally { movementLock.current = false; setSavingMovement(false); }
  };

  const recoverAsset = async (asset) => {
    setSavingMovement(true);
    try {
      await api.post(`${ASSET_API_PATHS[EDITOR_TYPE_BY_ASSET_TYPE[asset.assetType]]}/${asset.id}/recover`);
      toast.success("Registro completado"); setAssetHistoryOpen(false); await fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || "No se pudo completar el registro. Puedes volver a intentar."); }
    finally { setSavingMovement(false); }
  };
  const reverseAssetMovement = async (entry) => {
    const reason = window.prompt("Motivo de la anulación. Se registrará el movimiento contrario y se conservará el original.");
    if (!reason?.trim()) return;
    setSavingMovement(true);
    try {
      const path = ASSET_API_PATHS[EDITOR_TYPE_BY_ASSET_TYPE[historyAsset.assetType]];
      await api.post(`${path}/${historyAsset.id}/operations/${entry.id}/reverse`, { reason, request_id: requestId() });
      toast.success("Operación anulada con historial"); setAssetHistoryOpen(false); await fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || "No se pudo anular la operación"); }
    finally { setSavingMovement(false); }
  };

  if (loading) {
    return (
      <div className="aorra-assets-loading" data-testid="assets-page-loading">
        <div className="aorra-assets-loading-spinner"></div>
      </div>
    );
  }

  const totalAssets = summary?.total_assets;
  const breakdown = summary?.breakdown || {};
  const investmentGroups = ["cdts", "savings_accounts", "severance", "pensions", "stocks"];
  const savingsAndInvestments = investmentGroups.some(key => breakdown[key]?.total === null)
    ? null : investmentGroups.reduce((total, key) => total + (breakdown[key]?.total || 0), 0);
  const totalAssetItems = [...loans, ...cdts, ...savingsAccounts, ...severances, ...pensions, ...goods, ...stocks].filter(asset => !asset.archived_at).length;
  const assetCurrencySymbol = currency === "COP" ? "$" : `${currency} `;
  const normalizedAssetSearch = assetSearch.trim().toLocaleLowerCase("es-CO");
  const matchesAssetSearch = (item, fields) => !normalizedAssetSearch || fields.some((field) =>
    String(item[field] ?? "").toLocaleLowerCase("es-CO").includes(normalizedAssetSearch)
  );
  const filteredLoans = loans.filter((item) => matchesAssetSearch(item, ["borrower_name", "notes", "due_date"]));
  const filteredCdts = cdts.filter((item) => matchesAssetSearch(item, ["bank_name", "notes", "maturity_date"]));
  const filteredSavingsAccounts = savingsAccounts.filter((item) => matchesAssetSearch(item, ["account_name", "bank_name", "notes"]));
  const filteredSeverances = severances.filter((item) => matchesAssetSearch(item, ["fund_name", "employer", "notes"]));
  const filteredPensions = pensions.filter((item) => matchesAssetSearch(item, ["fund_name", "employer", "notes"]));

  const openAssetEditor = (asset) => {
    const {
      assetType, filterType, iconSrc, title, subtitle, valueLabel, value, detailLabel, detailValue, ...record
    } = asset;
    const editorType = EDITOR_TYPE_BY_ASSET_TYPE[assetType];
    setEditingItem(null);
    setWizardEditingAsset(record);
    setOpeningDetails({ account_id: "", transaction_date: record.opening_date || today(), request_id: requestId(), adjustment_reason: "" });
    setAssetCurrency(record.currency || currency);
    setNewAssetType(editorType);
    setNewAssetName(record.name || record.account_name || record.bank_name || record.borrower_name || record.fund_name || "");
    if (editorType === "loans") setLoanForm(record);
    if (editorType === "cdt") setCdtForm(record);
    if (editorType === "yield_accounts") setSavingsForm(record);
    if (editorType === "severance") setSeveranceForm(record);
    if (editorType === "pension") setPensionForm(record);
    if (editorType === "goods") setGoodForm(record);
    if (editorType === "stocks") setStockForm(record);
    setAssetWizardStep(2);
    setAssetWizardOpen(true);
  };

  const openAssetHistory = (asset) => {
    setHistoryAsset(asset);
    setAssetMovement({ amount: "", currency: asset.currency || currency, type: DEFAULT_OPERATION[asset.assetType], rates: {}, account_id: "", transaction_date: today(), interest_amount: "", affects_balance: true, request_id: requestId() });
    setAssetHistoryOpen(true);
  };

  const assetCards = [
    ...filteredSavingsAccounts.map((account) => ({
      ...account,
      assetType: "savings",
      filterType: "yield_accounts",
      iconSrc: ASSET_ICONS.yield_accounts,
      title: account.account_name,
      subtitle: account.bank_name || "Cuenta de ahorro",
      valueLabel: "Ahorro actual",
      value: account.balance,
      detailLabel: "Rentabilidad",
      detailValue: account.interest_rate ? `${account.interest_rate}%` : "—",
    })),
    ...filteredCdts.map((cdt) => ({
      ...cdt,
      assetType: "cdt",
      filterType: "cdt",
      iconSrc: ASSET_ICONS.cdt,
      title: cdt.bank_name,
      subtitle: cdt.maturity_date ? `Vence el ${cdt.maturity_date.slice(0, 10)}` : "CDT",
      valueLabel: "Capital invertido",
      value: cdt.amount,
      detailLabel: "Rentabilidad",
      detailValue: cdt.interest_rate ? `${cdt.interest_rate}%` : "—",
    })),
    ...filteredLoans.map((loan) => ({
      ...loan,
      assetType: "loan",
      filterType: "loans",
      iconSrc: ASSET_ICONS.loans,
      title: loan.borrower_name,
      subtitle: loan.due_date ? `Vence el ${loan.due_date.slice(0, 10)}` : "Préstamo a tercero",
      valueLabel: "Saldo pendiente",
      value: (loan.amount || 0) - (loan.amount_paid || 0),
      detailLabel: "Interés",
      detailValue: loan.interest_rate ? `${loan.interest_rate}%` : "—",
    })),
    ...filteredSeverances.map((severance) => ({
      ...severance,
      assetType: "severance",
      filterType: "severance",
      iconSrc: ASSET_ICONS.yield_accounts,
      title: severance.fund_name,
      subtitle: severance.employer || "Cesantías",
      valueLabel: "Saldo actual",
      value: severance.balance,
      detailLabel: "Aportes laborales",
      detailValue: "Registrados",
    })),
    ...filteredPensions.map((pension) => ({
      ...pension,
      assetType: "pension",
      filterType: "pension",
      iconSrc: ASSET_ICONS.yield_accounts,
      title: pension.fund_name,
      subtitle: pension.employer || "Pensión",
      valueLabel: "Saldo acumulado",
      value: pension.balance,
      detailLabel: "Aporte mensual",
      detailValue: pension.monthly_contribution ? `${pension.currency || currency} ${formatCurrency(pension.monthly_contribution, pension.currency || currency)}` : "—",
    })),
    ...goods.filter((item) => matchesAssetSearch(item, ["name", "notes"])).map((good) => ({
      ...good,
      assetType: "good",
      filterType: "goods",
      iconSrc: ASSET_ICONS.goods,
      title: good.name,
      subtitle: "Bien registrado",
      valueLabel: "Valor estimado",
      value: good.estimated_value,
      detailLabel: "Tipo",
      detailValue: "Bien",
    })),
    ...stocks.filter((item) => matchesAssetSearch(item, ["name", "notes"])).map((stock) => ({
      ...stock,
      assetType: "stock",
      filterType: "stocks",
      iconSrc: ASSET_ICONS.stocks,
      title: stock.name,
      subtitle: `${stock.quantity || 0} acciones`,
      valueLabel: "Valor actual",
      value: stock.current_value,
      detailLabel: "Precio actual",
      detailValue: `${stock.currency || currency} ${formatCurrency(stock.current_price || 0, stock.currency || currency)}`,
    })),
  ];
  const visibleAssetCards = assetCards.filter((asset) => !asset.archived_at && (assetTypeFilter === "all" || asset.filterType === assetTypeFilter));
  const hasActiveAssets = [...savingsAccounts, ...cdts, ...loans, ...severances, ...pensions, ...goods, ...stocks]
    .some((asset) => !asset.archived_at);
  const isFilteringAssets = Boolean(normalizedAssetSearch || assetTypeFilter !== "all");
  const selectedAssetTypeLabel = ASSET_TYPE_OPTIONS.find((option) => option.value === assetTypeFilter)?.label;
  const hasValue = (value) => value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value)) && Number(value) >= 0;
  const assetDetailsComplete = (() => {
    if (newAssetType === "cdt") return cdtForm.bank_name?.trim() && hasValue(cdtForm.amount) && hasValue(cdtForm.interest_rate) && cdtForm.start_date && cdtForm.maturity_date;
    if (newAssetType === "yield_accounts") return savingsForm.account_name?.trim() && savingsForm.bank_name?.trim() && hasValue(savingsForm.balance) && hasValue(savingsForm.interest_rate);
    if (newAssetType === "loans") return loanForm.borrower_name?.trim() && hasValue(loanForm.amount) && hasValue(loanForm.interest_rate);
    if (newAssetType === "severance") return severanceForm.fund_name?.trim() && hasValue(severanceForm.balance);
    if (newAssetType === "pension") return pensionForm.fund_name?.trim() && hasValue(pensionForm.balance);
    if (newAssetType === "goods") return goodForm.name?.trim() && hasValue(goodForm.estimated_value);
    if (newAssetType === "stocks") return stockForm.name?.trim() && hasValue(stockForm.quantity) && hasValue(stockForm.current_price);
    return false;
  })();
  const canContinueAssetWizard = Boolean(newAssetName.trim() && newAssetType);
  const openingAssetAmount = { cdt: cdtForm.amount, yield_accounts: savingsForm.balance, loans: loanForm.amount, severance: severanceForm.balance, pension: pensionForm.balance, goods: goodForm.estimated_value, stocks: Number(stockForm.quantity) * Number(stockForm.current_price) }[newAssetType] || 0;
  const canSaveAssetWizard = Boolean(assetDetailsComplete && (wizardEditingAsset || typeof ({ cdt: cdtForm, yield_accounts: savingsForm, loans: loanForm, severance: severanceForm, pension: pensionForm, goods: goodForm, stocks: stockForm }[newAssetType]?.affects_balance) === "boolean"));
  const assetHistoryEntries = (() => {
    if (!historyAsset) return [];
    const events = historyAsset.events || [];
    const ids = new Set(events.map(e => e.id));
    const reversed = new Set(events.map(e => e.reverses).filter(Boolean));
    const legacy = [...(historyAsset.payments || []), ...(historyAsset.transactions || []), ...(historyAsset.contributions || [])]
      .filter(e => !e.id || !ids.has(e.id)).map(e => ({ ...e, label: "Registro anterior", legacy: true }));
    return [...legacy, ...events.map(e => ({ ...e, label: OPERATION_NAMES[e.kind] || e.kind, reversed: reversed.has(e.id) }))];
  })();
  const lastReversible = [...assetHistoryEntries].reverse().find(e => e.id && e.kind !== "reversal" && !e.reversed)?.id;

  return (
    <div className="aorra-assets-page" data-testid="assets-page">
      <h1 className="aorra-assets-title">Mis Activos</h1>

      <section className="aorra-assets-kpi-grid" aria-label="Resumen de activos">
        <article className="aorra-assets-kpi-card">
          <div className="aorra-assets-kpi-icon"><img src={ASSET_ICONS.goods} alt="Patrimonio" /></div>
          <div className="aorra-assets-kpi-copy">
            <span>Activos registrados</span>
            <strong>{totalAssets == null ? "Sin valoración completa" : `${currency} ${formatCurrency(totalAssets, currency)}`}</strong>
          </div>
        </article>
        <article className="aorra-assets-kpi-card">
          <div className="aorra-assets-kpi-icon"><img src={ASSET_ICONS.cdt} alt="Total de activos" /></div>
          <div className="aorra-assets-kpi-copy">
            <span>Total Activos</span>
            <strong>{totalAssetItems}</strong>
          </div>
        </article>
        <article className="aorra-assets-kpi-card">
          <div className="aorra-assets-kpi-icon"><img src={ASSET_ICONS.yield_accounts} alt="Ahorro e inversión" /></div>
          <div className="aorra-assets-kpi-copy">
            <span>Ahorro e inversión</span>
            <strong>{savingsAndInvestments == null ? "Sin valoración completa" : `${assetCurrencySymbol}${formatCurrency(savingsAndInvestments, currency)}`}</strong>
          </div>
        </article>
        <article className="aorra-assets-kpi-card">
          <div className="aorra-assets-kpi-icon"><img src={ASSET_ICONS.goods} alt="Bienes" /></div>
          <div className="aorra-assets-kpi-copy">
            <span>Bienes</span>
            <strong>{breakdown.goods?.total === null ? "Sin valoración completa" : `${assetCurrencySymbol}${formatCurrency(breakdown.goods?.total || 0, currency)}`}</strong>
          </div>
        </article>
      </section>

      <section className="aorra-assets-toolbar" aria-label="Buscar y filtrar activos">
        <div className="aorra-assets-search">
          <Search aria-hidden="true" />
          <input
            type="text"
            value={assetSearch}
            onChange={(event) => setAssetSearch(event.target.value)}
            placeholder="Buscar activos..."
            aria-label="Buscar activos"
          />
        </div>
        <div className="aorra-assets-filter-controls">
          {assetTypeFilter !== "all" && (
            <div className="aorra-assets-active-filter">
              <span>{selectedAssetTypeLabel}</span>
              <button type="button" onClick={() => setAssetTypeFilter("all")} aria-label="Quitar filtro de tipo">
                <X aria-hidden="true" />
              </button>
            </div>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="aorra-assets-filter-button">
                <SlidersHorizontal aria-hidden="true" />
                Filtrar
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="aorra-assets-filter-popover">
              <label>Tipo de activo</label>
              <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de activo" />
                </SelectTrigger>
                <SelectContent className="aorra-assets-filter-select-content rounded-2xl border-outline-variant/20 shadow-xl overflow-hidden">
                  <SelectItem className="aorra-assets-filter-select-item" value="all">Todos</SelectItem>
                  {ASSET_TYPE_OPTIONS.map((option) => (
                    <SelectItem className="aorra-assets-filter-select-item" key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </PopoverContent>
          </Popover>
          <button type="button" className="aorra-assets-add-button" onClick={openAssetWizard}>
            <Plus aria-hidden="true" />
            Agregar activo
          </button>
        </div>
      </section>

      <section className="aorra-assets-all-grid" aria-label="Todos tus activos">
        {visibleAssetCards.length === 0 ? (
          <div className="aorra-assets-empty-grid">
            <AssetsEmptyIllustration />
            <h2>Sin resultados</h2>
            <p>{isFilteringAssets && hasActiveAssets ? "No encontramos activos que coincidan con tu búsqueda o filtros" : "Aún no tienes activos registrados"}</p>
            <button
              type="button"
              className="aorra-assets-empty-action"
              onClick={isFilteringAssets && hasActiveAssets
                ? () => { setAssetSearch(""); setAssetTypeFilter("all"); }
                : openAssetWizard}
            >
              {isFilteringAssets && hasActiveAssets ? (
                <X aria-hidden="true" />
              ) : (
                <Plus aria-hidden="true" />
              )}
              {isFilteringAssets && hasActiveAssets ? "Limpiar filtros" : "Agregar nuevo activo"}
            </button>
          </div>
        ) : (
          visibleAssetCards.map((asset) => (
            <article className="aorra-assets-square-card" key={`${asset.assetType}-${asset.id}`}>
              <header className="aorra-assets-square-card-head">
                <div className="aorra-assets-square-card-title">
                  <span className="aorra-assets-square-card-icon"><img src={asset.iconSrc} alt="" /></span>
                  <div>
                    <h2>{asset.title}</h2>
                    <p>{asset.subtitle}</p>
                  </div>
                </div>
                <button type="button" disabled={Boolean(asset.pending_operation || asset.archived_at)} className="aorra-assets-edit-button" onClick={() => openAssetEditor(asset)} aria-label={`Editar ${asset.title}`}>
                  <Edit2 aria-hidden="true" />
                </button>
              </header>

              <div className="aorra-assets-square-card-value">
                <span>{asset.pending_operation ? "Registro pendiente de completar" : asset.valueLabel}</span>
                <strong>{asset.currency || currency} {formatCurrency(asset.value || 0, asset.currency || currency)}</strong>
                {asset.currency && asset.currency !== currency && <small>≈ {currency} {(() => { const item = Object.values(breakdown).flatMap(b => b.items || []).find(i => i.id === asset.id); return item?.reporting_value == null ? "Sin tasa disponible" : formatCurrency(item.reporting_value, currency); })()} · {summary?.valuation_date}</small>}
              </div>

              <div className="aorra-assets-square-card-detail">
                <span>{asset.detailLabel}</span>
                <strong>{asset.detailValue}</strong>
              </div>

              <button type="button" className="aorra-assets-square-card-action" onClick={() => openAssetHistory(asset)}>Ver historial</button>
            </article>
          ))
        )}
        {visibleAssetCards.length > 0 && (
          <button type="button" className="aorra-assets-add-card" onClick={openAssetWizard}>
            <span><Plus aria-hidden="true" /></span>
            <strong>Agregar activo</strong>
            <small>Haz clic para comenzar</small>
          </button>
        )}
      </section>

      <Dialog open={assetWizardOpen} onOpenChange={setAssetWizardOpen}>
        <DialogContent className="aorra-assets-wizard" hideClose>
          <div className="aorra-assets-wizard-content">
            <button type="button" className="aorra-assets-wizard-close" onClick={() => setAssetWizardOpen(false)} aria-label="Cerrar"><X /></button>
            <DialogTitle>{assetWizardStep === 1 ? "Registrar nuevo activo" : `${wizardEditingAsset ? "Editar" : "Configura tu"} ${EDITOR_TYPE_LABELS[newAssetType]}`}</DialogTitle>

            {assetWizardStep === 1 ? (
              <div className="aorra-assets-wizard-body">
                <div className="aorra-assets-wizard-field">
                  <label>¿Cómo se llama tu activo?</label>
                  <Input value={newAssetName} onChange={(event) => setNewAssetName(event.target.value)} placeholder="Ej. CDT Bancolombia, apartamento o acciones" autoFocus />
                </div>
                <div className="aorra-assets-wizard-field">
                  <label>Selecciona el tipo de activo</label>
                  <Select value={newAssetType} onValueChange={setNewAssetType}>
                    <SelectTrigger><SelectValue placeholder="Selecciona un tipo" /></SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="aorra-assets-wizard-body">
              <CurrencySelect value={assetCurrency || currency} portalCurrency={currency} disabled={Boolean(wizardEditingAsset)} onChange={value => { setAssetCurrency(value); setAssetRates({}); }} label="Moneda del activo" />
              {!wizardEditingAsset && <>
                {newAssetType !== "cdt" && <label>Fecha de apertura<Input type="date" max={today()} value={openingDetails.transaction_date} onChange={e => setOpeningDetails({ ...openingDetails, transaction_date: e.target.value })} /></label>}
                <AccountSelect value={openingDetails.account_id} onChange={(value, account) => setOpeningDetails({ ...openingDetails, account_id: value, account_currency: account?.currency })} label="Cuenta de origen si salió de mi balance" />
                <FXPreview amount={openingAssetAmount} currency={assetCurrency || currency} targets={[currency, openingDetails.account_currency]} date={newAssetType === "cdt" ? cdtForm.start_date : openingDetails.transaction_date} rates={assetRates} onRatesChange={setAssetRates} />
              </>}
              {wizardEditingAsset && <label>Motivo del ajuste o valoración<Input value={openingDetails.adjustment_reason} onChange={e => setOpeningDetails({ ...openingDetails, adjustment_reason: e.target.value })} /><small>Una conciliación cambia el valor registrado; no representa una entrada o salida de dinero. Para mover dinero utiliza el historial.</small></label>}
              {newAssetType === "cdt" && <>
                <div className="aorra-assets-wizard-field"><label>Banco</label><Input value={cdtForm.bank_name} onChange={(event) => setCdtForm({ ...cdtForm, bank_name: event.target.value })} /></div>
                <div className="aorra-assets-wizard-two-cols"><div className="aorra-assets-wizard-field"><label>Monto invertido</label><CurrencyInput currency={assetCurrency || currency} value={cdtForm.amount} onChange={(event) => setCdtForm({ ...cdtForm, amount: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Tasa de interés (%)</label><Input type="number" value={cdtForm.interest_rate} onChange={(event) => setCdtForm({ ...cdtForm, interest_rate: event.target.value })} /></div></div>
                <div className="aorra-assets-wizard-two-cols"><div className="aorra-assets-wizard-field"><label>Fecha de apertura</label><Input type="date" value={cdtForm.start_date} onChange={(event) => setCdtForm({ ...cdtForm, start_date: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Fecha de vencimiento</label><Input type="date" value={cdtForm.maturity_date} onChange={(event) => setCdtForm({ ...cdtForm, maturity_date: event.target.value })} /></div></div>
              </>}
              {newAssetType === "yield_accounts" && <>
                <div className="aorra-assets-wizard-field"><label>Nombre de la cuenta</label><Input value={savingsForm.account_name} onChange={(event) => setSavingsForm({ ...savingsForm, account_name: event.target.value })} /></div>
                <div className="aorra-assets-wizard-two-cols"><div className="aorra-assets-wizard-field"><label>Banco</label><Input value={savingsForm.bank_name} onChange={(event) => setSavingsForm({ ...savingsForm, bank_name: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Saldo actual</label><CurrencyInput currency={assetCurrency || currency} value={savingsForm.balance} onChange={(event) => setSavingsForm({ ...savingsForm, balance: event.target.value })} /></div></div>
                <div className="aorra-assets-wizard-field"><label>Tasa de rendimiento (%)</label><Input type="number" value={savingsForm.interest_rate} onChange={(event) => setSavingsForm({ ...savingsForm, interest_rate: event.target.value })} /></div>
              </>}
              {newAssetType === "loans" && <>
                <div className="aorra-assets-wizard-field"><label>Nombre del deudor</label><Input value={loanForm.borrower_name} onChange={(event) => setLoanForm({ ...loanForm, borrower_name: event.target.value })} /></div>
                <div className="aorra-assets-wizard-two-cols"><div className="aorra-assets-wizard-field"><label>Monto prestado</label><CurrencyInput currency={assetCurrency || currency} value={loanForm.amount} onChange={(event) => setLoanForm({ ...loanForm, amount: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Tasa de interés (%)</label><Input type="number" value={loanForm.interest_rate} onChange={(event) => setLoanForm({ ...loanForm, interest_rate: event.target.value })} /></div></div>
                <div className="aorra-assets-wizard-field"><label>Fecha de vencimiento</label><Input type="date" value={loanForm.due_date} onChange={(event) => setLoanForm({ ...loanForm, due_date: event.target.value })} /></div>
              </>}
              {newAssetType === "severance" && <>
                <div className="aorra-assets-wizard-field"><label>Fondo</label><Input value={severanceForm.fund_name} onChange={(event) => setSeveranceForm({ ...severanceForm, fund_name: event.target.value })} /></div>
                <div className="aorra-assets-wizard-field"><label>Saldo actual</label><CurrencyInput currency={assetCurrency || currency} value={severanceForm.balance} onChange={(event) => setSeveranceForm({ ...severanceForm, balance: event.target.value })} /></div>
                <div className="aorra-assets-wizard-field"><label>Empresa (opcional)</label><Input value={severanceForm.employer || ""} onChange={(event) => setSeveranceForm({ ...severanceForm, employer: event.target.value })} /></div>
              </>}
              {newAssetType === "pension" && <>
                <div className="aorra-assets-wizard-field"><label>Fondo</label><Input value={pensionForm.fund_name} onChange={(event) => setPensionForm({ ...pensionForm, fund_name: event.target.value })} /></div>
                <div className="aorra-assets-wizard-field"><label>Saldo actual</label><CurrencyInput currency={assetCurrency || currency} value={pensionForm.balance} onChange={(event) => setPensionForm({ ...pensionForm, balance: event.target.value })} /></div>
                <div className="aorra-assets-wizard-field"><label>Empresa (opcional)</label><Input value={pensionForm.employer || ""} onChange={(event) => setPensionForm({ ...pensionForm, employer: event.target.value })} /></div>
              </>}
              {newAssetType === "goods" && <><div className="aorra-assets-wizard-field"><label>Nombre del bien</label><Input value={goodForm.name} onChange={(event) => setGoodForm({ ...goodForm, name: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Valor estimado</label><CurrencyInput currency={assetCurrency || currency} value={goodForm.estimated_value} onChange={(event) => setGoodForm({ ...goodForm, estimated_value: event.target.value })} /></div></>}
              {newAssetType === "stocks" && <><div className="aorra-assets-wizard-field"><label>Nombre de la acción</label><Input value={stockForm.name} onChange={(event) => setStockForm({ ...stockForm, name: event.target.value })} /></div><div className="aorra-assets-wizard-two-cols"><div className="aorra-assets-wizard-field"><label>Cantidad</label><Input type="number" value={stockForm.quantity} onChange={(event) => setStockForm({ ...stockForm, quantity: event.target.value })} /></div><div className="aorra-assets-wizard-field"><label>Precio actual</label><CurrencyInput currency={assetCurrency || currency} value={stockForm.current_price} onChange={(event) => setStockForm({ ...stockForm, current_price: event.target.value })} /></div></div></>}
              {!wizardEditingAsset && <>
                {newAssetType === "severance" && <BalanceOriginChoice value={severanceForm.affects_balance} onChange={affects_balance => setSeveranceForm({ ...severanceForm, affects_balance })} />}
                {newAssetType === "pension" && <BalanceOriginChoice value={pensionForm.affects_balance} onChange={affects_balance => setPensionForm({ ...pensionForm, affects_balance })} />}
                {newAssetType === "cdt" && <BalanceOriginChoice value={cdtForm.affects_balance} onChange={(affects_balance) => setCdtForm({ ...cdtForm, affects_balance })} />}
                {newAssetType === "yield_accounts" && <BalanceOriginChoice value={savingsForm.affects_balance} onChange={(affects_balance) => setSavingsForm({ ...savingsForm, affects_balance })} />}
                {newAssetType === "loans" && <BalanceOriginChoice value={loanForm.affects_balance} onChange={(affects_balance) => setLoanForm({ ...loanForm, affects_balance })} />}
                {newAssetType === "goods" && <BalanceOriginChoice value={goodForm.affects_balance} onChange={(affects_balance) => setGoodForm({ ...goodForm, affects_balance })} />}
                {newAssetType === "stocks" && <BalanceOriginChoice value={stockForm.affects_balance} onChange={(affects_balance) => setStockForm({ ...stockForm, affects_balance })} />}
              </>}
              </div>
            )}

            <div className="aorra-assets-wizard-footer">
              {assetWizardStep === 2 && <button type="button" className="aorra-assets-wizard-back" onClick={() => setAssetWizardStep(1)}>Atrás</button>}
              <button type="button" disabled={savingAsset || (assetWizardStep === 1 ? !canContinueAssetWizard : !canSaveAssetWizard)} className="aorra-assets-wizard-next" onClick={assetWizardStep === 1 ? continueAssetWizard : saveWizardAsset}>{assetWizardStep === 1 ? "Siguiente" : wizardEditingAsset ? "Guardar cambios" : "Guardar activo"}</button>
            </div>
            {wizardEditingAsset && <div className="aorra-assets-wizard-danger-zone"><button type="button" onClick={deleteWizardAsset}><Trash2 aria-hidden="true" /> Archivar activo</button></div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assetHistoryOpen} onOpenChange={setAssetHistoryOpen}>
        <DialogContent className="aorra-assets-history-dialog" hideClose>
          <div className="aorra-assets-history-content">
            <button type="button" className="aorra-assets-wizard-close" onClick={() => setAssetHistoryOpen(false)} aria-label="Cerrar"><X /></button>
            <DialogTitle>Historial de {historyAsset?.title || "activo"}</DialogTitle>
            <p>Estos son los movimientos registrados para este activo.</p>
            {historyAsset?.pending_operation ? <div role="alert"><p>Una operación quedó pendiente. Complétala antes de registrar otra para conservar los saldos.</p><Button disabled={savingMovement} onClick={() => recoverAsset(historyAsset)}>Completar registro pendiente</Button></div> : historyAsset && !historyAsset.archived_at && !["sold", "liquidated"].includes(historyAsset.status) && <div className="space-y-3 py-3">
              <p>{OPERATION_NAMES[assetMovement.type]}</p>
              {["savings", "pension", "severance"].includes(historyAsset.assetType) && <label>Operación<select className="border rounded p-2" aria-label="Tipo de movimiento" value={assetMovement.type} onChange={e => setAssetMovement({ ...assetMovement, type: e.target.value, affects_balance: true })}><option value={historyAsset.assetType === "savings" ? "deposit" : "contribution"}>Aporte o depósito</option><option value="withdrawal">Retiro</option></select></label>}
              {["sale", "liquidation"].includes(assetMovement.type) && <p>Registra el total recibido por el activo completo. Se conservarán la apertura, el capital y el resultado de la operación.</p>}
              <CurrencySelect value={assetMovement.currency} portalCurrency={currency} label="Moneda de la operación" onChange={value => setAssetMovement({ ...assetMovement, currency: value, rates: {} })} />
              <label>Monto total<CurrencyInput currency={assetMovement.currency} value={assetMovement.amount} onChange={e => setAssetMovement({ ...assetMovement, amount: e.target.value })} /></label>
              {historyAsset.assetType === "loan" && <label>Intereses incluidos en el total (opcional)<CurrencyInput currency={assetMovement.currency} value={assetMovement.interest_amount} onChange={e => setAssetMovement({ ...assetMovement, interest_amount: e.target.value })} /></label>}
              <label>Fecha<Input type="date" max={today()} value={assetMovement.transaction_date} onChange={e => setAssetMovement({ ...assetMovement, transaction_date: e.target.value })} /></label>
              {assetMovement.type === "contribution" && <label><input type="checkbox" checked={assetMovement.affects_balance !== false} onChange={e => setAssetMovement({ ...assetMovement, affects_balance: e.target.checked })} /> Salió de mi dinero disponible (desmarca si fue un aporte externo)</label>}
              {assetMovement.affects_balance !== false && <AccountSelect value={assetMovement.account_id} label={["deposit", "contribution"].includes(assetMovement.type) ? "Cuenta de origen" : "Cuenta que recibió el dinero"} onChange={(value, account) => setAssetMovement({ ...assetMovement, account_id: value, account_currency: account?.currency })} />}
              <FXPreview amount={assetMovement.amount} currency={assetMovement.currency} targets={[historyAsset.currency || currency, currency, assetMovement.account_currency]} date={assetMovement.transaction_date} rates={assetMovement.rates} onRatesChange={rates => setAssetMovement({ ...assetMovement, rates })} />
              <Button disabled={savingMovement || !(Number(assetMovement.amount) > 0)} onClick={saveAssetMovement}>{savingMovement ? "Guardando…" : "Registrar movimiento"}</Button>
            </div>}
            <div className="aorra-assets-history-list">
              {assetHistoryEntries.length === 0 ? (
                <div className="aorra-assets-history-empty">Aún no hay movimientos registrados para este activo.</div>
              ) : assetHistoryEntries.map((entry, index) => (
                <article key={`${entry.date || entry.created_at || "entry"}-${index}`} className="aorra-assets-history-entry">
                  <div><strong>{entry.label}</strong><span>{entry.date ? new Date(entry.date.length === 10 ? `${entry.date}T12:00:00` : entry.date).toLocaleDateString("es-CO") : "Sin fecha"}</span></div>
                  {entry.original_currency && entry.original_currency !== historyAsset?.currency && <small>Original: {entry.original_currency} {formatCurrency(entry.original_amount, entry.original_currency)} · {entry.fx_quotes?.[0]?.date}</small>}
                  {entry.amount != null && <b>{historyAsset?.currency || currency} {formatCurrency(entry.amount, historyAsset?.currency || currency)}</b>}
                  {entry.reason && <small>{entry.reason}</small>}
                  {entry.reversed && <small>Anulado · se conserva como evidencia</small>}
                  {entry.id === lastReversible && !entry.reversed && !historyAsset?.pending_operation && !historyAsset?.archived_at && <Button variant="outline" disabled={savingMovement} onClick={() => reverseAssetMovement(entry)}>Anular operación</Button>}
                </article>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Existing dialogs stay mounted here while the visible cards use the new unified grid. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="aorra-assets-tabs aorra-assets-legacy-tabs">
        {/* Loans Tab */}
        <TabsContent value="loans" className="mt-0">
          <Card className="aorra-assets-section">
            <CardHeader className="aorra-assets-section-head">
              <CardTitle className="aorra-assets-section-title">
                <HandCoins className="h-5 w-5 text-amber-500" />
                Préstamos a Terceros
              </CardTitle>
              <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingItem(null); setLoanForm({ borrower_name: "", amount: "", interest_rate: "", interest_type: "monthly", due_date: "", notes: "" }); }}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </DialogTrigger>
                <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-slate-800 dark:text-white">{editingItem ? "Editar Préstamo" : "Nuevo Préstamo"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Nombre del deudor</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={loanForm.borrower_name} onChange={e => setLoanForm({...loanForm, borrower_name: e.target.value})} placeholder="Juan Pérez" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Monto prestado</Label>
                      <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={loanForm.amount} onChange={e => setLoanForm({...loanForm, amount: e.target.value})} placeholder="500.000" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tasa de interés (%)</Label>
                        <Input type="number" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={loanForm.interest_rate} onChange={e => setLoanForm({...loanForm, interest_rate: e.target.value})} placeholder="5" />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tipo</Label>
                        <Select value={loanForm.interest_type} onValueChange={v => setLoanForm({...loanForm, interest_type: v})}>
                          <SelectTrigger className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Mensual</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Fecha de vencimiento (opcional)</Label>
                      <Input type="date" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={loanForm.due_date} onChange={e => setLoanForm({...loanForm, due_date: e.target.value})} />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Notas (opcional)</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={loanForm.notes} onChange={e => setLoanForm({...loanForm, notes: e.target.value})} placeholder="Préstamo personal" />
                    </div>
                    {!editingItem && (
                      <div className="flex items-start space-x-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <Checkbox 
                          id="loanAffectsBalance" 
                          checked={loanForm.affects_balance}
                          onCheckedChange={(checked) => setLoanForm({...loanForm, affects_balance: checked})}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label htmlFor="loanAffectsBalance" className="text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Este dinero saldrá de tu balance disponible?
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Marca esta opción si estás prestando dinero ahora. Desmarca si es un préstamo que ya habías hecho antes.
                          </p>
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={saveLoan}>Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {filteredLoans.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{normalizedAssetSearch ? "No encontramos préstamos que coincidan con tu búsqueda" : "No tienes préstamos registrados"}</p>
              ) : (
                <div className="space-y-3">
                  {filteredLoans.map(loan => {
                    const pending = loan.amount - (loan.amount_paid || 0);
                    const progress = ((loan.amount_paid || 0) / loan.amount) * 100;
                    return (
                      <div key={loan.id} className="aorra-assets-item">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800 dark:text-white">{loan.borrower_name}</span>
                              {loan.status === "paid" && <span className="text-xs bg-green-500/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded">Pagado</span>}
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Prestado: {currency} {formatCurrency(loan.amount, currency)}
                              {loan.interest_rate && ` • ${loan.interest_rate}% ${loan.interest_type === "monthly" ? "mensual" : "anual"}`}
                            </p>
                            {loan.due_date && <p className="text-xs text-slate-400 dark:text-slate-500">Vence: {loan.due_date.slice(0, 10)}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{currency} {formatCurrency(pending, currency)}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">pendiente</p>
                          </div>
                        </div>
                        {loan.amount_paid > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-amber-500 rounded-full" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{progress.toFixed(0)}% recuperado</p>
                          </div>
                        )}
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300" onClick={() => { setSelectedLoanForPayment(loan); setPaymentDialogOpen(true); }}>
                            <DollarSign className="h-3 w-3 mr-1" /> Registrar pago
                          </Button>
                          <Button size="sm" variant="ghost" className="text-slate-500 dark:text-slate-400" onClick={() => { setEditingItem(loan); setLoanForm(loan); setLoanDialogOpen(true); }}>
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => deleteLoan(loan.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CDTs Tab */}
        <TabsContent value="cdts" className="mt-0">
          <Card className="aorra-assets-section">
            <CardHeader className="aorra-assets-section-head">
              <CardTitle className="aorra-assets-section-title">
                <Landmark className="h-5 w-5 text-blue-500" />
                Certificados de Depósito (CDT)
              </CardTitle>
              <Dialog open={cdtDialogOpen} onOpenChange={setCdtDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={() => { setEditingItem(null); setCdtForm({ bank_name: "", amount: "", interest_rate: "", interest_type: "annual", start_date: "", maturity_date: "", notes: "" }); }}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </DialogTrigger>
                <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-slate-800 dark:text-white">{editingItem ? "Editar CDT" : "Nuevo CDT"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Banco</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={cdtForm.bank_name} onChange={e => setCdtForm({...cdtForm, bank_name: e.target.value})} placeholder="Bancolombia" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Monto invertido</Label>
                      <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={cdtForm.amount} onChange={e => setCdtForm({...cdtForm, amount: e.target.value})} placeholder="10.000.000" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tasa de interés (%)</Label>
                        <Input type="number" step="0.1" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={cdtForm.interest_rate} onChange={e => setCdtForm({...cdtForm, interest_rate: e.target.value})} placeholder="12.5" />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tipo</Label>
                        <Select value={cdtForm.interest_type} onValueChange={v => setCdtForm({...cdtForm, interest_type: v})}>
                          <SelectTrigger className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Mensual</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Fecha apertura</Label>
                        <Input type="date" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={cdtForm.start_date} onChange={e => setCdtForm({...cdtForm, start_date: e.target.value})} />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Fecha vencimiento</Label>
                        <Input type="date" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={cdtForm.maturity_date} onChange={e => setCdtForm({...cdtForm, maturity_date: e.target.value})} />
                      </div>
                    </div>
                    {!editingItem && (
                      <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <Checkbox 
                          id="cdtAffectsBalance" 
                          checked={cdtForm.affects_balance}
                          onCheckedChange={(checked) => setCdtForm({...cdtForm, affects_balance: checked})}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label htmlFor="cdtAffectsBalance" className="text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Este dinero saldrá de tu balance disponible?
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Marca esta opción si estás invirtiendo dinero ahora. Desmarca si es un CDT que ya tenías.
                          </p>
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-blue-500 hover:bg-blue-600" onClick={saveCdt}>Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {filteredCdts.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{normalizedAssetSearch ? "No encontramos CDTs que coincidan con tu búsqueda" : "No tienes CDTs registrados"}</p>
              ) : (
                <div className="space-y-3">
                  {filteredCdts.map(cdt => (
                    <div key={cdt.id} className="aorra-assets-item">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-800 dark:text-white">{cdt.bank_name}</span>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{cdt.interest_rate}% {cdt.interest_type === "annual" ? "E.A." : "mensual"}</p>
                          {cdt.maturity_date && <p className="text-xs text-slate-400 dark:text-slate-500">Vence: {cdt.maturity_date.slice(0, 10)}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{currency} {formatCurrency(cdt.amount, currency)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button size="sm" variant="ghost" className="text-slate-500 dark:text-slate-400" onClick={() => { setEditingItem(cdt); setCdtForm(cdt); setCdtDialogOpen(true); }}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => deleteCdt(cdt.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Savings Accounts Tab */}
        <TabsContent value="savings" className="mt-0">
          <Card className="aorra-assets-section">
            <CardHeader className="aorra-assets-section-head">
              <CardTitle className="aorra-assets-section-title">
                <PiggyBank className="h-5 w-5 text-pink-500" />
                Cuentas de Ahorro con Rendimiento
              </CardTitle>
              <Dialog open={savingsDialogOpen} onOpenChange={setSavingsDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-pink-500 hover:bg-pink-600" onClick={() => { setEditingItem(null); setSavingsForm({ account_name: "", bank_name: "", balance: "", interest_rate: "", interest_type: "annual", notes: "", affects_balance: false }); }}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </DialogTrigger>
                <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-slate-800 dark:text-white">{editingItem ? "Editar Cuenta" : "Nueva Cuenta"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Nombre de la cuenta</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={savingsForm.account_name} onChange={e => setSavingsForm({...savingsForm, account_name: e.target.value})} placeholder="Mi cuenta Nu" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Banco/Entidad</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={savingsForm.bank_name} onChange={e => setSavingsForm({...savingsForm, bank_name: e.target.value})} placeholder="Nu" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Saldo actual</Label>
                      <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={savingsForm.balance} onChange={e => setSavingsForm({...savingsForm, balance: e.target.value})} placeholder="2.000.000" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tasa de rendimiento (%)</Label>
                        <Input type="number" step="0.1" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={savingsForm.interest_rate} onChange={e => setSavingsForm({...savingsForm, interest_rate: e.target.value})} placeholder="13" />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-200">Tipo</Label>
                        <Select value={savingsForm.interest_type} onValueChange={v => setSavingsForm({...savingsForm, interest_type: v})}>
                          <SelectTrigger className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Mensual</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {!editingItem && (
                      <div className="flex items-start space-x-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <Checkbox 
                          id="savingsAffectsBalance" 
                          checked={savingsForm.affects_balance}
                          onCheckedChange={(checked) => setSavingsForm({...savingsForm, affects_balance: checked})}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label htmlFor="savingsAffectsBalance" className="text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Este dinero saldrá de tu balance disponible?
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Marca esta opción si vas a transferir dinero a esta cuenta ahora. Desmarca si ya tenías este dinero ahorrado.
                          </p>
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-pink-500 hover:bg-pink-600" onClick={saveSavingsAccount}>Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {filteredSavingsAccounts.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{normalizedAssetSearch ? "No encontramos cuentas que coincidan con tu búsqueda" : "No tienes cuentas de ahorro registradas"}</p>
              ) : (
                <div className="space-y-3">
                  {filteredSavingsAccounts.map(acc => (
                    <div key={acc.id} className="aorra-assets-item">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-800 dark:text-white">{acc.account_name}</span>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{acc.bank_name} • {acc.interest_rate}% {acc.interest_type === "annual" ? "E.A." : "mensual"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-pink-600 dark:text-pink-400">{currency} {formatCurrency(acc.balance, currency)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button size="sm" variant="ghost" className="text-slate-500 dark:text-slate-400" onClick={() => { setEditingItem(acc); setSavingsForm(acc); setSavingsDialogOpen(true); }}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => deleteSavingsAccount(acc.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Severance Tab */}
        <TabsContent value="severance" className="mt-0">
          <Card className="aorra-assets-section">
            <CardHeader className="aorra-assets-section-head">
              <CardTitle className="aorra-assets-section-title">
                <Building2 className="h-5 w-5 text-purple-500" />
                Cesantías
              </CardTitle>
              <Dialog open={severanceDialogOpen} onOpenChange={setSeveranceDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-purple-500 hover:bg-purple-600" onClick={() => { setEditingItem(null); setSeveranceForm({ fund_name: "", balance: "", employer: "", notes: "", affects_balance: false }); }}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </DialogTrigger>
                <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-slate-800 dark:text-white">{editingItem ? "Editar Cesantías" : "Nuevas Cesantías"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Fondo</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={severanceForm.fund_name} onChange={e => setSeveranceForm({...severanceForm, fund_name: e.target.value})} placeholder="Porvenir, Protección, etc." />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Saldo actual</Label>
                      <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={severanceForm.balance} onChange={e => setSeveranceForm({...severanceForm, balance: e.target.value})} placeholder="5.000.000" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Empresa (opcional)</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={severanceForm.employer} onChange={e => setSeveranceForm({...severanceForm, employer: e.target.value})} placeholder="Mi Empresa SAS" />
                    </div>
                    {!editingItem && (
                      <div className="flex items-start space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <Checkbox 
                          id="severanceAffectsBalance" 
                          checked={severanceForm.affects_balance}
                          onCheckedChange={(checked) => setSeveranceForm({...severanceForm, affects_balance: checked})}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label htmlFor="severanceAffectsBalance" className="text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Este dinero saldrá de tu balance disponible?
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Las cesantías normalmente son aportes del empleador. Deja desmarcado si no sale de tu dinero.
                          </p>
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-purple-500 hover:bg-purple-600" onClick={saveSeverance}>Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {filteredSeverances.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{normalizedAssetSearch ? "No encontramos cesantías que coincidan con tu búsqueda" : "No tienes cesantías registradas"}</p>
              ) : (
                <div className="space-y-3">
                  {filteredSeverances.map(sev => (
                    <div key={sev.id} className="aorra-assets-item">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-800 dark:text-white">{sev.fund_name}</span>
                          {sev.employer && <p className="text-sm text-slate-500 dark:text-slate-400">{sev.employer}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{currency} {formatCurrency(sev.balance, currency)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button size="sm" variant="ghost" className="text-slate-500 dark:text-slate-400" onClick={() => { setEditingItem(sev); setSeveranceForm(sev); setSeveranceDialogOpen(true); }}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => deleteSeverance(sev.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pension Tab */}
        <TabsContent value="pension" className="mt-0">
          <Card className="aorra-assets-section">
            <CardHeader className="aorra-assets-section-head">
              <CardTitle className="aorra-assets-section-title">
                <DollarSign className="h-5 w-5 text-teal-500" />
                Pensiones
              </CardTitle>
              <Dialog open={pensionDialogOpen} onOpenChange={setPensionDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-teal-500 hover:bg-teal-600" onClick={() => { setEditingItem(null); setPensionForm({ fund_name: "", balance: "", contribution_type: "mandatory", employer: "", notes: "", affects_balance: false }); }}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </DialogTrigger>
                <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-slate-800 dark:text-white">{editingItem ? "Editar Pensión" : "Nueva Pensión"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Fondo</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={pensionForm.fund_name} onChange={e => setPensionForm({...pensionForm, fund_name: e.target.value})} placeholder="Protección, Porvenir, etc." />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Saldo acumulado</Label>
                      <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={pensionForm.balance} onChange={e => setPensionForm({...pensionForm, balance: e.target.value})} placeholder="15.000.000" />
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Tipo de aporte</Label>
                      <Select value={pensionForm.contribution_type} onValueChange={v => setPensionForm({...pensionForm, contribution_type: v})}>
                        <SelectTrigger className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mandatory">Obligatorio</SelectItem>
                          <SelectItem value="voluntary">Voluntario</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-700 dark:text-slate-200">Empresa (opcional)</Label>
                      <Input className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={pensionForm.employer} onChange={e => setPensionForm({...pensionForm, employer: e.target.value})} placeholder="Mi Empresa SAS" />
                    </div>
                    {!editingItem && (
                      <div className="flex items-start space-x-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                        <Checkbox 
                          id="pensionAffectsBalance" 
                          checked={pensionForm.affects_balance}
                          onCheckedChange={(checked) => setPensionForm({...pensionForm, affects_balance: checked})}
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label htmlFor="pensionAffectsBalance" className="text-sm font-medium text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Este dinero saldrá de tu balance disponible?
                          </label>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Si es pensión obligatoria, el empleador aporta la mayoría. Deja desmarcado si no sale de tu dinero.
                          </p>
                        </div>
                      </div>
                    )}
                    <Button className="w-full bg-teal-500 hover:bg-teal-600" onClick={savePension}>Guardar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {filteredPensions.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{normalizedAssetSearch ? "No encontramos pensiones que coincidan con tu búsqueda" : "No tienes pensiones registradas"}</p>
              ) : (
                <div className="space-y-3">
                  {filteredPensions.map(pen => (
                    <div key={pen.id} className="aorra-assets-item">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-slate-800 dark:text-white">{pen.fund_name}</span>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{pen.contribution_type === "mandatory" ? "Obligatorio" : "Voluntario"}{pen.employer && ` • ${pen.employer}`}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-teal-600 dark:text-teal-400">{currency} {formatCurrency(pen.balance, currency)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button size="sm" variant="ghost" className="text-slate-500 dark:text-slate-400" onClick={() => { setEditingItem(pen); setPensionForm(pen); setPensionDialogOpen(true); }}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => deletePension(pen.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="aorra-assets-dialog bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-white">Registrar Pago de {selectedLoanForPayment?.borrower_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-200">Monto recibido</Label>
              <CurrencyInput currency="COP" className="bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="100.000" />
            </div>
            <Button className="w-full bg-green-500 hover:bg-green-600" onClick={recordLoanPayment}>Registrar Pago</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
